import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type {
	useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation,
	useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation$data,
} from '@atlassian/jira-relay/src/__generated__/useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation.graphql';
import { CONNECT_PROJECTS, PACKAGE_NAME, TEAM_NAME } from '../../common/errors-constants.tsx';
import { messages } from '../../common/messages.tsx';
import type { ConnectedProjectProxy } from '../../common/types.tsx';
import { useGenericFlag } from '../use-generic-flag/index.tsx';
import { useHandleError } from '../use-handle-error/index.tsx';
import { updateStore } from './update-store/index.tsx';

export const useConnectProjects = (currentProjectAri: string) => {
	const { formatMessage } = useIntl();
	const handleError = useHandleError();
	const showGenericFlag = useGenericFlag();
	const reportError = (error: Error) =>
		fireErrorAnalytics({
			meta: {
				id: CONNECT_PROJECTS,
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});

	const [connectProjectMutation, isConnectInFlight] =
		useMutation<useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation>(graphql`
			mutation useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation(
				$input: GraphStoreCreateProjectHasRelatedWorkWithProjectInput
			) {
				graphStore @optIn(to: "GraphStoreMutation") {
					createProjectHasRelatedWorkWithProject(input: $input)
						@optIn(to: "GraphStoreProjectHasRelatedWorkWithProject") {
						success
						errors {
							message
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`);

	const connectProjects = useCallback(
		(toProject: ConnectedProjectProxy) =>
			new Promise<void>((resolve, reject) => {
				connectProjectMutation({
					variables: {
						input: {
							relationships: [
								{
									from: currentProjectAri,
									to: toProject.resourceId,
								},
							],
						},
					},
					onCompleted(
						response: useConnectProjectProjectHasRelatedWorkWithProjectCreateMutation$data,
					) {
						try {
							handleError(response.graphStore?.createProjectHasRelatedWorkWithProject, {
								404: {
									title: formatMessage(messages.projectConnectionNotFoundTitle),
									description: formatMessage(messages.projectConnectionNotFoundDescription),
								},
								403: {
									title: formatMessage(messages.connectPermissionsFailFlagTitle),
									description: formatMessage(messages.connectPermissionsFailFlagDescription),
								},
							});
						} catch (err) {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							reportError(err as Error);
							reject(err);
							return;
						}
						resolve();
					},
					onError(err) {
						reportError(err);
						showGenericFlag();
						reject();
					},
					updater: (store, data) => {
						if (data?.graphStore?.createProjectHasRelatedWorkWithProject?.success) {
							updateStore(store, [toProject], currentProjectAri);
						}
					},
				});
			}),
		[connectProjectMutation, currentProjectAri, handleError, formatMessage, showGenericFlag],
	);

	return [connectProjects, isConnectInFlight] as const;
};
